import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import React__default, { forwardRef, useState, useRef, useCallback, useEffect } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import ExpandedFdocContent from './ExpandedFdocContent.js';
import { ExpandedFdocProvider } from './ExpandedFdocProvider.js';
import ExpandedFdocSidebar from './ExpandedFdocSidebar.js';
import { ContentSkeleton, SidebarSkeleton } from './ExpandedFdocLoading.js';
import { useResourceDataContext } from '../context/resourceDataContext.js';
import { styled } from 'styled-components';
import { ExpandedFdocElements } from './ExpandedFdocElements.js';
import { isResourceDetailFolder, isResourceDetailNotepad } from '../../../resources/utils/resourceDetailGuards.js';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { isInteractiveElement } from '@fabric-app/utils/element';
import { Modal, defaultModalContentProps } from '@fabric-app/ui/components/Modal/Modal';
import { Button } from '@fabric-app/ui/components/Button';
import { SocketIOParentRoomProvider } from '../../../socket.io/ParentRoom/SocketIOParentRoomProvider.js';
import { IconArrow } from '@fabric-app/icons/svg';
import { useExpandedResourceState, useShowPreviousExpandedResource } from '../../../resources/stores/useExpandedResourceStore.js';
import { ResourceRoomProvider } from '../../../multiplayer/config/fdocs.config.js';
import { SubDocumentSearchProvider } from '../../../search/context/SubDocumentSearchContext.js';
import { useContextShareToken } from '../../../permission/contexts/AnonShareTokenContext.js';
import { mediaTablet } from '@fabric-app/ui/constants/styled-utils';
import { windowBlur } from '../../../utils/windowBlur.js';

const ItemPreviewWrapper = styled.section.withConfig({
  displayName: "ExpandedFdoc__ItemPreviewWrapper",
  componentId: "sc-9eea45e8-0"
})([
  `position:fixed;top:0;left:0;right:0;bottom:0;z-index:125;pointer-events:auto;display:flex;align-items:center;justify-content:center;padding:10px;@media screen and (min-width:1025px){&[data-sidebar-expanded='true']{padding:10px 10px 10px 52px;}}`,
  `{padding:0;}.todesktop-platform-darwin &{top:25px;}&[data-no-gestures='true']{pointer-events:none;}`
], mediaTablet);
const ItemPreviewBG = styled.div.withConfig({
  displayName: "ExpandedFdoc__ItemPreviewBG",
  componentId: "sc-9eea45e8-1"
})([
  `z-index:-1;position:absolute;top:0;left:0;width:100%;height:100%;background-color:`,
  `;`
], cssVar["color-bg-overlay"]);
const ItemPreview = styled.div.withConfig({
  displayName: "ExpandedFdoc__ItemPreview",
  componentId: "sc-9eea45e8-2"
})([
  `padding-top:calc(env(safe-area-inset-top,0px) + var(--android-inset-top,0px));transition:0.2s transform;z-index:1;position:relative;display:flex;align-items:center;color:black;justify-content:flex-end;gap:52px;width:100%;height:100%;`
]);
const ExpandedFdoc = /* @__PURE__ */ forwardRef(({ onClose, className, hideBackdrop = false, hideComments = false, draftSaveFdoc, draftOnSelectTag, draftTags, style, noGestures, contentStyle, overrideSidebarOpen, setOverrideSidebarOpen, extra, resourceId, goToNextResource, goToPrevResource, disablePortalRender, forceFullScreen }, ref) => {
  const [pendingChanges, setPendingChanges] = useState(false);
  const [isSimilar, setIsSimilarOpen] = useState(false);
  const shareToken = useContextShareToken();
  const [_isFullscreen, setIsFullscreen] = useState(false);
  const isFullscreen = forceFullScreen ? true : _isFullscreen;
  const { resource } = useResourceDataContext();
  const { expandedResourceSidebarOpen: storedExpandedFdocSidebarOpen } = useExpandedResourceState();
  const [closing, setClosing] = useState(false);
  const pendingChangesRef = useRef(pendingChanges);
  pendingChangesRef.current = pendingChanges;
  const handleClose = useCallback(async () => {
    if (isSimilar) {
      setIsSimilarOpen(false);
      return;
    }
    if (!onClose) return;
    const promise = new Promise((resolve) => {
      if (pendingChangesRef.current) {
        setClosing(true);
        setTimeout(() => {
          onClose?.();
          resolve();
        }, 420);
      } else onClose?.();
    });
    return promise;
  }, [
    isSimilar,
    onClose,
    pendingChangesRef
  ]);
  const [showCommentsMobile, setShowCommentsMobile] = React__default.useState(false);
  const toggleComments = () => setShowCommentsMobile((s) => !s);
  useEffect(() => {
    if (!resource || !isResourceDetailFolder(resource)) return;
    onClose?.();
  }, [
    resource,
    onClose
  ]);
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  useEffect(() => {
    if (noGestures) return;
    const handleKeyDown = (e) => {
      if (e.key !== "Escape") return;
      if (isInteractiveElement(document.activeElement, true) || document.activeElement?.getAttribute("contenteditable") === "true" || document.activeElement?.closest("[data-slate-control]")) {
        const element = document.activeElement;
        element.blur();
        return;
      }
      e.stopPropagation();
      e.preventDefault();
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      handleClose();
    };
    window.addEventListener("keydown", handleKeyDown, {
      capture: true
    });
    return () => window.removeEventListener("keydown", handleKeyDown, {
      capture: true
    });
  }, [
    noGestures,
    handleClose
  ]);
  useEffect(() => {
    if (!goToPrevResource && !goToNextResource || !resource || noGestures) return;
    const handleKeyDown = (e) => {
      if (isInteractiveElement(document.activeElement) && document.activeElement?.role !== "tab") return;
      if (isResourceDetailNotepad(resource) && isEditorFocused) return;
      if (e.key === "ArrowLeft" && goToPrevResource) {
        e.preventDefault();
        goToPrevResource();
      } else if (e.key === "ArrowRight" && goToNextResource) {
        e.preventDefault();
        goToNextResource();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    noGestures,
    resource,
    goToNextResource,
    goToPrevResource,
    isEditorFocused
  ]);
  const hasFdoc = resource !== null;
  const [sidebarRef, setSidebarRef] = useState(null);
  const [rootRef, setRootRef] = useState(null);
  const [splitContentElement, setSplitContentElement] = useState(null);
  useEffect(() => {
    if (noGestures || !rootRef) return;
    window.requestAnimationFrame(() => {
      windowBlur();
    });
  }, [
    noGestures,
    rootRef
  ]);
  const fdocTitle = resource ? resource.name : "Item";
  if (!hasFdoc || closing) return null;
  const MaybePortal = disablePortalRender ? React__default.Fragment : Modal.Portal;
  return /* @__PURE__ */ jsx(ExpandedFdocProvider, {
    overrideSidebarOpen,
    setOverrideSidebarOpen,
    resourceId,
    handleClose,
    children: /* @__PURE__ */ jsx(SubDocumentSearchProvider, {
      children: /* @__PURE__ */ jsx(Modal.Root, {
        open: true,
        children: /* @__PURE__ */ jsx(MaybePortal, {
          children: /* @__PURE__ */ jsx(DialogPrimitive.Content, {
            style: {
              outline: "none"
            },
            asChild: true,
            "aria-describedby": void 0,
            ...defaultModalContentProps,
            children: /* @__PURE__ */ jsxs(ItemPreviewWrapper, {
              "data-no-gestures": noGestures,
              "data-sidebar-expanded": storedExpandedFdocSidebarOpen,
              className,
              ref: (node) => {
                setRootRef(node);
                if (typeof ref === "function") {
                  ref(node);
                } else if (ref) {
                  ref.current = node;
                }
              },
              style,
              children: [
                /* @__PURE__ */ jsx(VisuallyHidden.Root, {
                  asChild: true,
                  children: /* @__PURE__ */ jsx(DialogPrimitive.Title, {
                    children: fdocTitle
                  })
                }),
                extra,
                !isFullscreen && /* @__PURE__ */ jsx(ButtonPreviousResource, {}),
                !hideBackdrop && /* @__PURE__ */ jsx(ItemPreviewBG, {
                  onClick: handleClose
                }),
                /* @__PURE__ */ jsxs(ItemPreview, {
                  style: contentStyle,
                  id: "expanded-fdoc",
                  children: [
                    /* @__PURE__ */ jsx(ExpandedFdocElements.Closer, {
                      onClick: handleClose
                    }),
                    resource ? /* @__PURE__ */ jsx(Fragment, {
                      children: /* @__PURE__ */ jsx(SocketIOParentRoomProvider, {
                        stayConnectedWhenDocumentNotVisible: true,
                        enabled: !resource.isDraft && shareToken?.type !== "publishing",
                        room: {
                          path: "resources",
                          params: {
                            resourceId: resource.id
                          }
                        },
                        children: /* @__PURE__ */ jsxs(ResourceRoomProvider, {
                          resourceId: resource.id,
                          children: [
                            /* @__PURE__ */ jsx(ExpandedFdocContent, {
                              onClose: onClose ? handleClose : void 0,
                              sidebarRef,
                              rootRef,
                              setPendingChanges,
                              isEditorFocused,
                              setIsEditorFocused,
                              draftSaveFdoc,
                              draftOnSelectTag,
                              draftTags,
                              isFullscreen,
                              setIsFullscreen,
                              ref: setSplitContentElement,
                              disableGestures: noGestures,
                              hideBackdrop
                            }),
                            !hideComments && /* @__PURE__ */ jsx(ExpandedFdocSidebar, {
                              onClose: handleClose,
                              showCommentsMobile,
                              toggleComments,
                              ref: setSidebarRef,
                              isFullscreen,
                              splitContentElement,
                              disableGestures: noGestures
                            })
                          ]
                        })
                      })
                    }) : /* @__PURE__ */ jsxs(Fragment, {
                      children: [
                        /* @__PURE__ */ jsx(ContentSkeleton, {
                          onClose: handleClose
                        }),
                        !hideComments && /* @__PURE__ */ jsx(SidebarSkeleton, {})
                      ]
                    })
                  ]
                })
              ]
            })
          })
        })
      })
    })
  });
});
ExpandedFdoc.displayName = "ExpandedFdoc";
const ButtonPreviousResourceBase = styled(Button).attrs({
  shape: "square",
  variant: "bg-primary-reverse",
  size: "sm"
}).withConfig({
  displayName: "ExpandedFdoc__ButtonPreviousResourceBase",
  componentId: "sc-9eea45e8-3"
})([
  `position:fixed;top:10px;left:10px;border-radius:50%;z-index:100;`
]);
const ButtonPreviousResource = () => {
  const showPreviousResource = useShowPreviousExpandedResource();
  if (showPreviousResource) {
    return /* @__PURE__ */ jsx(ButtonPreviousResourceBase, {
      onClick: showPreviousResource,
      children: /* @__PURE__ */ jsx(IconArrow, {
        style: {
          fontSize: "1.35rem"
        }
      })
    });
  }
  return null;
};

export { ExpandedFdoc as default };
//# sourceMappingURL=ExpandedFdoc.js.map
